/**
 * Ladder-aware Priority panel (CIO 2026-10-27, options work item 2; HelionBackend strategy/ladder_priority.py).
 *
 * The Screener's Priority order is rung status first (room / on target / full against a sweet-spot-tilted target
 * measured at COST BASIS), then the nearest tenor to 24-30m, then Entry Score. This panel shows WHY: the
 * suggested next buy in plain English, and one tile per buyable expiry with its share of the held LEAPS book
 * against its target, its status and its live board spread.
 *
 * A pure readout: it renders no control and places nothing. The CIO decides what and how much to buy.
 * When the backend could not read the held book (or the rule is switched off) it says so, and the page keeps
 * the tenor-then-score Priority order.
 */

import React from "react";

export type LadderRung = {
  expiry: string;
  dte?: number | null;
  bucket?: string | null;
  bucketLabel?: string | null;
  heldLines?: number | null;
  heldContracts?: number | null;
  heldAmount?: number | null;
  sharePct?: number | null;
  targetPct?: number | null;
  gapPct?: number | null;
  toTargetUsd?: number | null;
  status?: "room" | "on_target" | "full" | "outside_ladder" | string | null;
  statusLabel?: string | null;
  statusWhy?: string | null;
  boardMedianSpreadPct?: number | null;
  wideBoard?: boolean | null;
  leavesBucketInDays?: number | null;
  rollingOff?: boolean | null;
  priority?: number | null;
};

export type LadderNextBuy = {
  expiry: string;
  conid?: string | number | null;
  strike?: number | null;
  right?: string | null;
  mid?: number | null;
  spreadPct?: number | null;
  status?: string | null;
  statusLabel?: string | null;
  reason?: string | null;
  notes?: string[] | null;
  toTargetUsd?: number | null;
  contractsToTargetAtMid?: number | null;
  discretion?: string | null;
  // Backend may add (2026-10-27 Phase 1 contract): rungs with room the suggestion passed over, and why.
  passedOver?: { expiry: string; why?: string | null }[] | null;
};

export type LadderPriority = {
  active?: boolean | null;
  mode?: string | null;
  fallback?: boolean | null;
  note?: string | null;
  basis?: string | null;
  bookSource?: string | null;
  bookTotal?: number | null;
  bandPct?: number | null;
  capPct?: number | null;
  wideBoardSpreadPct?: number | null;
  rungs?: LadderRung[] | null;
  otherHeld?: { expiry: string; heldAmount?: number | null; sharePct?: number | null; heldLines?: number | null }[] | null;
  nextBuy?: LadderNextBuy | null;
  rule?: string | null;
};

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

export function expiryLabel(iso: string | null | undefined): string {
  const s = String(iso || "");
  const m = /^(\d{4})-(\d{2})/.exec(s);
  if (!m) return s || "—";
  return `${MONTHS[Number(m[2]) - 1] ?? m[2]}${m[1]}`;
}

const pct = (n: number | null | undefined, digits = 1) =>
  typeof n === "number" && Number.isFinite(n) ? `${(n * 100).toFixed(digits)}%` : "—";

const usd = (n: number | null | undefined) =>
  typeof n === "number" && Number.isFinite(n)
    ? n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    : "—";

const STATUS_STYLE: Record<string, { bg: string; border: string; label: string }> = {
  room: { bg: "rgba(16, 185, 129, 0.14)", border: "rgba(16, 185, 129, 0.40)", label: "Room" },
  on_target: { bg: "rgba(148, 163, 184, 0.14)", border: "rgba(148, 163, 184, 0.35)", label: "On target" },
  full: { bg: "rgba(245, 158, 11, 0.14)", border: "rgba(245, 158, 11, 0.40)", label: "Full" },
  outside_ladder: { bg: "rgba(148, 163, 184, 0.10)", border: "rgba(148, 163, 184, 0.25)", label: "Outside ladder" },
};

// A rung with room that drops below 18 months within 90 days ranks AFTER the on-target rungs (ladder_priority
// sort_group). A green "Room" chip on those rows read as "buy" while they ranked 14-27 (audit 2026-10-27, finding 7),
// so the chip says "rolling off" in amber instead.
const ROLLING_OFF_STYLE = { bg: "rgba(245, 158, 11, 0.10)", border: "rgba(245, 158, 11, 0.40)" };

export function rungChipLabel(status: string | null | undefined, rollingOff?: boolean | null): string {
  if (!status) return "";
  const base = (STATUS_STYLE[status] ?? STATUS_STYLE.outside_ladder).label;
  return rollingOff ? `${base} · rolling off` : base;
}

export function RungStatusChip({ status, rollingOff }: { status?: string | null; rollingOff?: boolean | null }) {
  if (!status) return null;
  const st = STATUS_STYLE[status] ?? STATUS_STYLE.outside_ladder;
  const look = rollingOff && status === "room" ? ROLLING_OFF_STYLE : st;
  return (
    <span
      data-testid="rung-status-chip"
      data-status={status}
      data-rolling-off={rollingOff ? "true" : undefined}
      className="ml-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ backgroundColor: look.bg, border: `1px solid ${look.border}` }}
      title={
        rollingOff
          ? "This expiry drops below 18 months within 90 days, so it ranks after the on-target rungs even with room (ladder target at cost basis)."
          : "This contract's expiry against the ladder target (cost basis): Room = 3+ pts under target, Full = 3+ pts over it or above the per-expiry cap."
      }
    >
      {rungChipLabel(status, rollingOff)}
    </span>
  );
}

export function strikesQualifyText(n: number | null | undefined): string {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return "No strike qualifies this run";
  return n === 1 ? "1 strike qualifies" : `${n} strikes qualify`;
}

export default function LadderPriorityPanel({
  ladder,
  textPrimary,
  textMuted,
  cardBorder,
  strikesByExpiry,
}: {
  ladder: LadderPriority | null | undefined;
  textPrimary?: string;
  textMuted?: string;
  cardBorder?: string;
  /** Phase 2: ranked strikes per expiry, folded into each rung tile (replaces the Ladder by Expiration card). */
  strikesByExpiry?: Record<string, number> | null;
}) {
  if (!ladder) return null;
  const muted = textMuted ? { color: textMuted } : undefined;
  const strong = textPrimary ? { color: textPrimary } : undefined;
  const border = cardBorder || "rgba(148, 163, 184, 0.3)";

  if (!ladder.active) {
    return (
      <div
        data-testid="ladder-priority-fallback"
        className="rounded-lg px-3 py-2 text-xs"
        style={{ border: "1px solid rgba(245, 158, 11, 0.40)", backgroundColor: "rgba(245, 158, 11, 0.10)" }}
      >
        <span className="font-semibold">Ladder view unavailable.</span>{" "}
        {ladder.note || "Priority is tenor first, then Entry Score."}
      </div>
    );
  }

  const rungs = ladder.rungs || [];
  const nb = ladder.nextBuy;
  const maxShare = Math.max(0.0001, ...rungs.map((r) => Math.max(r.sharePct ?? 0, r.targetPct ?? 0)));

  return (
    <div data-testid="ladder-priority-panel" className="rounded-lg px-3 py-3" style={{ border: `1px solid ${border}` }}>
      {nb ? (
        <div data-testid="ladder-next-buy" className="mb-3">
          <div className="text-xs font-semibold uppercase tracking-wide" style={muted}>
            Suggested next buy
          </div>
          <div className="mt-1 text-sm font-semibold" style={strong}>
            SPX {expiryLabel(nb.expiry)} {typeof nb.strike === "number" ? nb.strike.toFixed(0) : "—"} {nb.right || "C"}
            <span className="font-normal" style={muted}>
              {" "}
              · mid {typeof nb.mid === "number" ? `$${nb.mid.toFixed(2)}` : "—"} · quoted {pct(nb.spreadPct)} wide
            </span>
            <RungStatusChip status={nb.status} />
          </div>
          <div className="mt-1 text-sm" style={strong}>
            {nb.reason}
          </div>
          {(nb.passedOver || []).length ? (
            <div className="mt-1 text-xs" style={muted} data-testid="ladder-next-buy-passed-over">
              Passed over:{" "}
              {(nb.passedOver || [])
                .map((po) => `${expiryLabel(po.expiry)}${po.why ? ` (${po.why})` : ""}`)
                .join(" · ")}
            </div>
          ) : null}
          {typeof nb.toTargetUsd === "number" && nb.toTargetUsd > 0 ? (
            <div className="mt-1 text-xs" style={muted} data-testid="ladder-next-buy-gap">
              Gap to target at cost: <span className="font-semibold" style={strong}>{usd(nb.toTargetUsd)}</span>
              {typeof nb.contractsToTargetAtMid === "number"
                ? nb.contractsToTargetAtMid < 1
                  ? " (less than one contract at today's mid)"
                  : ` (about ${nb.contractsToTargetAtMid.toFixed(1)} contracts at today's mid)`
                : ""}
            </div>
          ) : null}
          {(nb.notes || []).map((n) => (
            <div key={n} className="mt-1 text-xs" data-testid="ladder-next-buy-note" style={{ color: "rgb(217, 119, 6)" }}>
              {n}
            </div>
          ))}
          <div className="mt-1 text-[11px] italic" style={muted}>
            {nb.discretion}
          </div>
        </div>
      ) : null}

      <div className="text-xs font-semibold uppercase tracking-wide" style={muted}>
        The ladder at {ladder.basis || "cost basis"} · Priority order left to right
      </div>
      <div className="mt-2 grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
        {rungs.map((r) => {
          const st = STATUS_STYLE[String(r.status)] ?? STATUS_STYLE.outside_ladder;
          const shareW = `${Math.min(100, ((r.sharePct ?? 0) / maxShare) * 100)}%`;
          const targetL = `${Math.min(100, ((r.targetPct ?? 0) / maxShare) * 100)}%`;
          return (
            <div
              key={r.expiry}
              data-testid="ladder-rung"
              data-expiry={r.expiry}
              data-status={r.status || ""}
              className="rounded-md px-2 py-2 text-xs"
              style={{ backgroundColor: st.bg, border: `1px solid ${st.border}` }}
              title={r.statusWhy || undefined}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold" style={strong}>
                  #{r.priority ?? "—"} {expiryLabel(r.expiry)}
                </span>
                <span className="font-semibold">{STATUS_STYLE[String(r.status)]?.label ?? r.statusLabel}</span>
              </div>
              <div style={muted}>
                {r.bucketLabel || r.bucket} · {typeof r.dte === "number" ? `${r.dte} DTE` : ""}
              </div>
              <div className="mt-1">
                <span className="font-semibold" style={strong}>
                  {pct(r.sharePct)}
                </span>{" "}
                of book vs <span className="font-semibold">{pct(r.targetPct)}</span> target
              </div>
              <div className="relative mt-1 h-1.5 w-full rounded" style={{ backgroundColor: "rgba(148, 163, 184, 0.20)" }}>
                <div className="h-1.5 rounded" style={{ width: shareW, backgroundColor: "rgba(59, 130, 246, 0.75)" }} />
                <div
                  className="absolute top-[-3px] h-3 w-0.5"
                  style={{ left: targetL, backgroundColor: textPrimary || "currentColor" }}
                  title="target"
                />
              </div>
              <div className="mt-1" style={muted}>
                {r.statusWhy}
              </div>
              {strikesByExpiry ? (
                <div className="mt-1" style={muted} data-testid="ladder-rung-strikes">
                  {strikesQualifyText(strikesByExpiry[r.expiry])}
                </div>
              ) : null}
              <div className="mt-1">
                Board spread {pct(r.boardMedianSpreadPct)}
                {r.wideBoard ? (
                  <span data-testid="ladder-wide-board" className="ml-1 font-semibold" style={{ color: "rgb(217, 119, 6)" }}>
                    · wide board
                  </span>
                ) : null}
              </div>
              {r.rollingOff ? (
                <div className="mt-1 font-semibold" style={{ color: "rgb(217, 119, 6)" }}>
                  Drops below 18 months within 90 days
                </div>
              ) : r.bucket === "24_30m" && typeof r.leavesBucketInDays === "number" ? (
                <div className="mt-1" style={muted}>
                  Leaves 24-30m in {r.leavesBucketInDays} days
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {(ladder.otherHeld || []).length ? (
        <div className="mt-2 text-xs" style={muted} data-testid="ladder-other-held">
          Also held, not bought new:{" "}
          {(ladder.otherHeld || []).map((o) => `${expiryLabel(o.expiry)} ${pct(o.sharePct)}`).join(" · ")}
        </div>
      ) : null}
      <div className="mt-2 text-[11px]" style={muted}>
        {ladder.rule} Book: {ladder.bookSource || "held LEAPS"}, total {usd(ladder.bookTotal)} at cost. Bands ±
        {pct(ladder.bandPct, 0)}; per-expiry cap {pct(ladder.capPct, 0)}.
      </div>
    </div>
  );
}
